import { apiFetch, apiFetchRaw } from './client.js';

/** What the platform needs somebody in the console to look at. */
export interface AdminNotification {
  id: string;
  kind: string;
  severity: 'INFO' | 'ACTION_REQUIRED';
  title: string;
  body: string | null;
  /** Console path this is about, so it is one click from the work. */
  link: string | null;
  entityType: string | null;
  entityId: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationPage {
  items: AdminNotification[];
  unread: number;
}

export const notificationsApi = {
  list: async (limit = 30): Promise<NotificationPage> => {
    const envelope = await apiFetchRaw<AdminNotification[]>(
      `/admin/notifications?limit=${String(limit)}`,
    );
    return { items: envelope.data ?? [], unread: envelope.meta?.unread ?? 0 };
  },

  markRead: (ids?: readonly string[]): Promise<{ marked: number }> =>
    apiFetch<{ marked: number }>('/admin/notifications/read', {
      method: 'POST',
      body: ids === undefined ? {} : { ids },
    }),
};

/** One kind of thing that happens, from the platform's own catalogue. */
export interface AlertEvent {
  key: string;
  group: 'ONBOARDING' | 'DEPOSITS' | 'WITHDRAWALS' | 'COMPLIANCE' | 'ACCOUNTS' | 'TREASURY';
  label: string;
  detail: string;
  /** False when nothing in the platform raises this yet. */
  live: boolean;
}

/** A named Slack destination. The webhook is never returned. */
export interface AlertChannel {
  id: string;
  name: string;
  isActive: boolean;
  createdAt: string;
}

export interface AlertRoute {
  eventKey: string;
  channelId: string | null;
  isActive: boolean;
}

export const alertsApi = {
  events: (): Promise<AlertEvent[]> => apiFetch<AlertEvent[]>('/admin/notifications/events'),

  routing: (): Promise<{ channels: AlertChannel[]; routes: AlertRoute[] }> =>
    apiFetch<{ channels: AlertChannel[]; routes: AlertRoute[] }>('/admin/notifications/channels'),

  saveChannel: (input: {
    id?: string;
    name: string;
    webhookUrl?: string;
    isActive?: boolean;
  }): Promise<{ id: string }> =>
    apiFetch<{ id: string }>('/admin/notifications/channels', { method: 'POST', body: input }),

  deleteChannel: (id: string): Promise<{ deleted: boolean }> =>
    apiFetch<{ deleted: boolean }>(`/admin/notifications/channels/${id}`, { method: 'DELETE' }),

  /** Every route the save bar changed, saved together and recorded as one History entry (DEV-137). */
  setRoutes: (
    routes: ReadonlyArray<{ eventKey: string; channelId: string | null }>,
    reason?: string,
  ): Promise<{ routes: Array<{ eventKey: string; channelId: string | null }> }> =>
    apiFetch('/admin/notifications/routes', {
      method: 'PUT',
      body: { routes, ...(reason === undefined ? {} : { reason }) },
    }),
};
